import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import {
  Ticket, Books, HardDrives, Users, Warning, FunnelSimple, CaretRight, Cpu, Code, Package,
  GearSix, ChartPieSlice, SquaresFour, Rows, Stack, ClockCountdown, UsersThree, CheckCircle,
  CurrencyInr, TrendUp,
} from '@phosphor-icons/react';
import {
  ModuleShell,
  SkeletonBlock,
  FilterChips,
  StatusDonut,
  CategoryPie,
  VolumeBar,
  WorkloadBar,
  SlaRiskDonut,
} from './module-ui';
import { apiPath } from '../config/apiBase';
import { mergeOrder, reorderIds, dragHandlers } from './dashboard/dragOrder';
import ProductTeamsWidget from './dashboard/ProductTeamsWidget';
import PerformanceByTicketsWidget from './dashboard/PerformanceByTicketsWidget';
import {
  SolvedPerMonthWidget,
  SolvedByApplicationWidget,
  SolvedByPersonWidget,
} from './dashboard/ApplicationResolutionsWidget';

const TICKETS_API = apiPath('/api/tickets');
const API = apiPath('/api');

const TIME_RANGES = [
  { value: 'all', label: 'All Time', days: null },
  { value: '7d', label: 'Last 7 Days', days: 7 },
  { value: '30d', label: 'Last 30 Days', days: 30 },
  { value: '90d', label: 'Last 90 Days', days: 90 },
];

const VIEW_MODES = [
  { id: 'pictorial', label: 'Pictorial', icon: ChartPieSlice, desc: 'Charts & graphs only' },
  { id: 'cards', label: 'Cards', icon: SquaresFour, desc: 'Number cards' },
  { id: 'compact', label: 'Compact', icon: Rows, desc: 'Dense KPI strip' },
  { id: 'hybrid', label: 'Hybrid', icon: Stack, desc: 'Charts + cards' },
];

const WIDGET_DEFS = [
  { id: 'statusChart', label: 'Status doughnut', group: 'charts' },
  { id: 'domainChart', label: 'Tickets by domain', group: 'charts' },
  { id: 'appChart', label: 'Tickets by product', group: 'charts' },
  { id: 'clientChart', label: 'Tickets by client', group: 'charts' },
  { id: 'volumeChart', label: 'Volume bar', group: 'charts' },
  { id: 'slaChart', label: 'SLA risk (pictorial)', group: 'charts' },
  { id: 'teamChart', label: 'Team by product', group: 'charts' },
  { id: 'performanceChart', label: 'Performance by tickets', group: 'charts' },
  { id: 'solvedPerMonthChart', label: 'Tickets solved per month', group: 'charts' },
  { id: 'solvedByAppChart', label: 'Tickets solved by application', group: 'charts' },
  { id: 'solvedByPersonChart', label: 'Who solved the tickets', group: 'charts' },
  { id: 'econProductChart', label: 'Revenue vs spend by product', group: 'charts' },
  { id: 'econClientChart', label: 'Client revenue vs spend', group: 'charts' },
  { id: 'econMixChart', label: 'Spend by product', group: 'charts' },
  { id: 'assetTypeChart', label: 'Assets by type', group: 'charts' },
  { id: 'assetStatusChart', label: 'Assets by status', group: 'charts' },
  { id: 'kpiTotal', label: 'Total tickets', group: 'kpis' },
  { id: 'kpiOpen', label: 'Open tickets', group: 'kpis' },
  { id: 'kpiInProgress', label: 'In progress', group: 'kpis' },
  { id: 'kpiClosed', label: 'Closed tickets', group: 'kpis' },
  { id: 'kpiCompleted', label: 'Completed', group: 'kpis' },
  { id: 'kpiRcaCompliance', label: 'RCA compliance', group: 'kpis' },
  { id: 'kpiMyTasks', label: 'My tasks', group: 'kpis' },
  { id: 'kpiSlaBreached', label: 'SLA breached', group: 'kpis' },
  { id: 'kpiSlaAtRisk', label: 'SLA at risk', group: 'kpis' },
  { id: 'kpiHardware', label: 'Hardware open', group: 'kpis' },
  { id: 'kpiSoftware', label: 'Software open', group: 'kpis' },
  { id: 'kpiProduct', label: 'App Support open', group: 'kpis' },
  { id: 'kpiTopProduct', label: 'Top product open', group: 'kpis' },
  { id: 'kpiTopClient', label: 'Top client open', group: 'kpis' },
  { id: 'kpiTeamMembers', label: 'Team members', group: 'kpis' },
  { id: 'kpiTeamActive', label: 'Team active work', group: 'kpis' },
  { id: 'kpiTeamClosed', label: 'Team closed total', group: 'kpis' },
  { id: 'kpiKnowledge', label: 'Knowledge articles', group: 'kpis' },
  { id: 'kpiAssets', label: 'Total assets', group: 'kpis' },
  { id: 'kpiAssetsAssigned', label: 'Assets assigned', group: 'kpis' },
  { id: 'kpiAssetsInfra', label: 'Client infrastructure', group: 'kpis' },
  { id: 'kpiAssetPurchase', label: 'Asset purchase value', group: 'kpis' },
  { id: 'kpiRevenue', label: 'Client revenue (mo)', group: 'kpis' },
  { id: 'kpiSpend', label: 'Delivery spend (mo)', group: 'kpis' },
  { id: 'kpiMargin', label: 'Monthly margin', group: 'kpis' },
  { id: 'kpiClientsBilled', label: 'Clients with billing', group: 'kpis' },
  { id: 'kpiUsers', label: 'Total users', group: 'kpis' },
];

const CHART_IDS = WIDGET_DEFS.filter((w) => w.group === 'charts').map((w) => w.id);
const KPI_IDS = WIDGET_DEFS.filter((w) => w.group === 'kpis').map((w) => w.id);

/** Revenue / portfolio economics — Overview only for super_admin. */
const SUPER_ADMIN_ONLY_WIDGETS = new Set([
  'econProductChart',
  'econClientChart',
  'econMixChart',
  'assetTypeChart',
  'assetStatusChart',
  'kpiAssetsAssigned',
  'kpiAssetsInfra',
  'kpiAssetPurchase',
  'kpiRevenue',
  'kpiSpend',
  'kpiMargin',
  'kpiClientsBilled',
]);
const MIN_COLS = 1;
const MAX_COLS = 6;
const DEFAULT_COLS = 3;

function clampColumns(value) {
  const n = Number.parseInt(value, 10);
  if (!Number.isFinite(n)) return DEFAULT_COLS;
  return Math.min(MAX_COLS, Math.max(MIN_COLS, n));
}

const DEFAULT_PREFS = {
  viewMode: 'pictorial',
  widgets: Object.fromEntries(WIDGET_DEFS.map((w) => [w.id, true])),
  chartOrder: [...CHART_IDS],
  kpiOrder: [...KPI_IDS],
  columnsPerRow: DEFAULT_COLS,
};

function prefsKey(userId) {
  return `wd_dashboard_prefs_${userId || 'anon'}`;
}

function loadPrefs(userId) {
  try {
    const raw = localStorage.getItem(prefsKey(userId));
    if (!raw) {
      return {
        ...DEFAULT_PREFS,
        widgets: { ...DEFAULT_PREFS.widgets },
        chartOrder: [...DEFAULT_PREFS.chartOrder],
        kpiOrder: [...DEFAULT_PREFS.kpiOrder],
        columnsPerRow: DEFAULT_COLS,
      };
    }
    const parsed = JSON.parse(raw);
    return {
      viewMode: VIEW_MODES.some((v) => v.id === parsed.viewMode) ? parsed.viewMode : 'pictorial',
      widgets: {
        ...DEFAULT_PREFS.widgets,
        ...(parsed.widgets || {}),
        ...(parsed.widgets?.slaPipeline != null && parsed.widgets?.slaChart == null
          ? { slaChart: parsed.widgets.slaPipeline }
          : {}),
      },
      chartOrder: mergeOrder(parsed.chartOrder, CHART_IDS),
      kpiOrder: mergeOrder(parsed.kpiOrder, KPI_IDS),
      columnsPerRow: clampColumns(parsed.columnsPerRow ?? DEFAULT_COLS),
    };
  } catch {
    return {
      ...DEFAULT_PREFS,
      widgets: { ...DEFAULT_PREFS.widgets },
      chartOrder: [...DEFAULT_PREFS.chartOrder],
      kpiOrder: [...DEFAULT_PREFS.kpiOrder],
      columnsPerRow: DEFAULT_COLS,
    };
  }
}

function OverviewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [byStatus, setByStatus] = useState({});
  const [trend, setTrend] = useState([]);
  const [slaSummary, setSlaSummary] = useState(null);
  const [breakdown, setBreakdown] = useState({ by_domain: [], by_app: [], by_client: [] });
  const [productTeams, setProductTeams] = useState([]);
  const [performance, setPerformance] = useState({ items: [], max_closed: 1 });
  const [economics, setEconomics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ priority: 'all', timeRange: '30d' });
  const [prefs, setPrefs] = useState(() => loadPrefs(user?.user_id));
  const [showCustomize, setShowCustomize] = useState(false);
  const [draggingId, setDraggingId] = useState(null);

  useEffect(() => {
    setPrefs(loadPrefs(user?.user_id));
  }, [user?.user_id]);

  const savePrefs = useCallback((next) => {
    setPrefs(next);
    try {
      localStorage.setItem(prefsKey(user?.user_id), JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }, [user?.user_id]);

  const authConfig = () => {
    const token = localStorage.getItem('token');
    return {
      withCredentials: true,
      headers: { Authorization: `Bearer ${token}` },
    };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const config = authConfig();
      const range = TIME_RANGES.find((r) => r.value === filters.timeRange);
      const params = {};
      if (filters.priority !== 'all') params.priority = filters.priority;
      if (range && range.days) params.days = range.days;

      const trendDays = range?.days || 30;

      const [statsRes, legacyStatsRes, statusRes, trendsRes, slaRes, breakdownRes, teamsRes, perfRes, econRes] = await Promise.all([
        axios.get(`${TICKETS_API}/dashboard/summary`, { ...config, params }).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/stats`, { ...config, params }).catch(() => ({ data: null })),
        axios.get(`${TICKETS_API}/dashboard/by-status`, config).catch(() => ({ data: {} })),
        axios.get(`${TICKETS_API}/dashboard/trends`, { ...config, params: { days: trendDays } }).catch(() => ({ data: { trend: [] } })),
        axios.get(`${TICKETS_API}/dashboard/sla`, config).catch(() => ({ data: null })),
        axios.get(`${TICKETS_API}/dashboard/breakdown`, { ...config, params: { open_only: true, limit: 12 } }).catch(() => ({ data: null })),
        axios.get(`${TICKETS_API}/dashboard/product-teams`, { ...config, params: { limit_products: 12 } }).catch(() => ({ data: { items: [] } })),
        axios.get(`${TICKETS_API}/dashboard/performance`, { ...config, params: { limit: 12 } }).catch(() => ({ data: { items: [] } })),
        user?.role === 'super_admin'
          ? axios.get(`${API}/assets/economics/overview`, config).catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ]);

      const ticketStats = statsRes.data || {};
      const legacy = legacyStatsRes.data || {};
      const sla = slaRes.data || {};
      const econ = econRes.data || null;
      const assetSnap = econ?.assets || null;

      setStats({
        ...legacy,
        ...ticketStats,
        total_tickets: ticketStats.total_tickets ?? ticketStats.total ?? legacy.total_tickets ?? 0,
        open_tickets: ticketStats.open_tickets ?? ticketStats.open ?? legacy.open_tickets ?? 0,
        in_progress_tickets: ticketStats.in_progress_tickets ?? ticketStats.in_progress ?? 0,
        completed: ticketStats.completed ?? 0,
        closed: ticketStats.closed ?? 0,
        my_tasks: ticketStats.my_tasks ?? ticketStats.my_tickets ?? legacy.my_tickets ?? 0,
        sla_at_risk: ticketStats.sla_at_risk ?? 0,
        sla_breached: ticketStats.sla_breached ?? sla.breached ?? 0,
        rca: ticketStats.rca || null,
        total_assets: assetSnap?.total ?? legacy.total_assets ?? 0,
        assets_assigned: assetSnap?.assigned ?? 0,
        assets_infra: assetSnap?.infra_count ?? 0,
        assets_purchase_value: assetSnap?.purchase_value ?? 0,
        by_domain: ticketStats.by_domain || {
          hardware: ticketStats.hardware_open ?? 0,
          software: ticketStats.software_open ?? 0,
          product: ticketStats.product_open ?? 0,
        },
      });
      setEconomics(econ);
      setSlaSummary({
        breached: ticketStats.sla_breached ?? sla.breached ?? 0,
        at_risk: ticketStats.sla_at_risk ?? 0,
        on_track: Math.max(
          (sla.with_sla ?? 0)
            - (ticketStats.sla_breached ?? sla.breached ?? 0)
            - (ticketStats.sla_at_risk ?? 0),
          0,
        ),
        with_sla: sla.with_sla ?? 0,
        compliance_pct: sla.compliance_pct,
      });
      setByStatus(statusRes.data || {});
      setTrend(
        (trendsRes.data?.trend || []).map((t) => ({
          label: (t.date || '').slice(5),
          date: t.date,
          created: t.count,
          count: t.count,
        })),
      );
      setBreakdown({
        by_domain: breakdownRes.data?.by_domain || [],
        by_app: breakdownRes.data?.by_app || [],
        by_client: breakdownRes.data?.by_client || [],
      });
      setProductTeams(teamsRes.data?.items || []);
      setPerformance({
        items: perfRes.data?.items || [],
        max_closed: perfRes.data?.max_closed || 1,
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const drillPipeline = (extra = {}) => {
    const sp = new URLSearchParams();
    Object.entries(extra).forEach(([k, v]) => {
      if (v != null && v !== '') sp.set(k, v);
    });
    // Prefer rich 360° modes for domain / client / volume / product.
    if (extra.domain && !extra.mode) sp.set('mode', 'domain');
    if (extra.client && !extra.mode) sp.set('mode', 'client');
    const qs = sp.toString();
    navigate(qs ? `/dashboard/tickets/pipeline?${qs}` : '/dashboard/tickets/pipeline');
  };

  const ChartFullLink = ({ to, label = 'Full view' }) => (
    <button
      type="button"
      className="wd-perf-all-pipeline"
      onClick={() => navigate(to)}
      style={{ marginLeft: 'auto' }}
    >
      {label} <CaretRight size={12} weight="bold" />
    </button>
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.priority, filters.timeRange, user?.role]);

  const ticketQueryBase = useMemo(() => {
    const sp = new URLSearchParams();
    if (filters.priority !== 'all') sp.set('priority', filters.priority);
    return sp;
  }, [filters]);

  const buildLink = (extra = {}) => {
    const sp = new URLSearchParams(ticketQueryBase);
    Object.entries(extra).forEach(([k, v]) => sp.set(k, v));
    const qs = sp.toString();
    return qs ? `?${qs}` : '';
  };

  const domainBreakdown = stats?.by_domain || {};
  const topProduct = breakdown.by_app[0] || null;
  const topClient = breakdown.by_client[0] || null;
  const teamMemberTotal = productTeams.reduce((n, p) => n + (p.member_count || 0), 0);
  const teamActiveTotal = productTeams.reduce((n, p) => n + (p.active_total || 0), 0);
  const teamClosedTotal = productTeams.reduce((n, p) => n + (p.closed_total || 0), 0);
  const econSummary = economics?.summary || {};
  const econCurrency = econSummary.currency || 'INR';

  const money = (n) => {
    if (n == null || Number.isNaN(Number(n))) return '—';
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: econCurrency,
        maximumFractionDigits: 0,
      }).format(Number(n));
    } catch {
      return `${econCurrency} ${Number(n).toLocaleString('en-IN')}`;
    }
  };

  const canSeeEconomics = user?.role === 'super_admin';
  const canSeeOrgAssets = ['super_admin', 'admin', 'manager'].includes(user?.role);

  const statusForChart = useMemo(() => {
    if (byStatus && Object.keys(byStatus).length) return byStatus;
    return {
      open: stats?.open_tickets || 0,
      in_progress: stats?.in_progress_tickets || 0,
      completed: stats?.completed || 0,
      closed: stats?.closed || 0,
    };
  }, [byStatus, stats]);

  const filterChips = [];
  if (filters.priority !== 'all') {
    filterChips.push({
      id: 'priority',
      label: `Priority: ${filters.priority}`,
      onRemove: () => setFilters((f) => ({ ...f, priority: 'all' })),
    });
  }
  if (filters.timeRange !== 'all') {
    const label = TIME_RANGES.find((r) => r.value === filters.timeRange)?.label || filters.timeRange;
    filterChips.push({
      id: 'time',
      label,
      onRemove: () => setFilters((f) => ({ ...f, timeRange: 'all' })),
    });
  }

  const show = (id) => prefs.widgets[id] !== false;
  const mode = prefs.viewMode || 'pictorial';
  const showCharts = mode === 'pictorial' || mode === 'hybrid';
  const showCards = mode === 'cards' || mode === 'hybrid';
  const showTinyKpis = mode === 'compact';

  const chartOrder = mergeOrder(prefs.chartOrder, CHART_IDS);
  const kpiOrder = mergeOrder(prefs.kpiOrder, KPI_IDS);

  const allKpis = [
    {
      id: 'kpiTotal',
      label: 'Total Tickets',
      value: stats?.total_tickets || 0,
      icon: Ticket,
      color: '#002FA7',
      to: `/dashboard/tickets/list${buildLink()}`,
      testid: 'stat-card-total-tickets',
    },
    {
      id: 'kpiOpen',
      label: 'Open Tickets',
      value: stats?.open_tickets || 0,
      icon: Warning,
      color: '#F59E0B',
      to: `/dashboard/tickets/list${buildLink({ status_group: 'open' })}`,
      testid: 'stat-card-open-tickets',
    },
    {
      id: 'kpiInProgress',
      label: 'In Progress',
      value: stats?.in_progress_tickets || 0,
      icon: Ticket,
      color: '#3B82F6',
      to: `/dashboard/tickets/list${buildLink({ status: 'in_progress' })}`,
      testid: 'stat-card-in-progress',
    },
    {
      id: 'kpiClosed',
      label: 'Closed',
      value: stats?.closed || 0,
      icon: CheckCircle,
      color: '#6B7280',
      to: `/dashboard/tickets/list${buildLink({ status_group: 'closed' })}`,
      testid: 'stat-card-closed',
    },
    {
      id: 'kpiCompleted',
      label: 'Completed',
      value: stats?.completed || 0,
      icon: CheckCircle,
      color: '#059669',
      to: `/dashboard/tickets/list${buildLink({ status: 'completed' })}`,
      testid: 'stat-card-completed',
    },
    {
      id: 'kpiRcaCompliance',
      label: 'RCA Compliance',
      value: stats?.rca ? `${stats.rca.compliance_pct ?? 0}%` : '—',
      icon: CheckCircle,
      color: '#7C3AED',
      to: '/dashboard/tickets/rca',
      testid: 'stat-card-rca-compliance',
      hint: stats?.rca
        ? `${stats.rca.with_rca ?? 0} of ${stats.rca.total ?? 0} tickets have full RCA`
        : 'Root cause analysis coverage',
    },
    {
      id: 'kpiMyTasks',
      label: 'My Tasks',
      value: stats?.my_tasks || 0,
      icon: Ticket,
      color: '#10B981',
      to: '/dashboard/tickets/my-tasks',
      testid: 'stat-card-my-tasks',
    },
    {
      id: 'kpiSlaBreached',
      label: 'SLA Breached',
      value: stats?.sla_breached || 0,
      icon: Warning,
      color: '#DC2626',
      to: '/dashboard/sla?tab=pipeline&state=breached',
      testid: 'stat-card-sla-breached',
    },
    {
      id: 'kpiSlaAtRisk',
      label: 'SLA At Risk',
      value: stats?.sla_at_risk || 0,
      icon: ClockCountdown,
      color: '#D97706',
      to: '/dashboard/sla?tab=pipeline&state=at_risk',
      testid: 'stat-card-sla-at-risk',
    },
    {
      id: 'kpiHardware',
      label: 'Hardware (Open)',
      value: domainBreakdown.hardware ?? 0,
      icon: Cpu,
      color: '#8B5CF6',
      to: '/dashboard/tickets/pipeline?domain=hardware',
      testid: 'stat-card-hardware',
    },
    {
      id: 'kpiSoftware',
      label: 'Software (Open)',
      value: domainBreakdown.software ?? 0,
      icon: Code,
      color: '#3B82F6',
      to: '/dashboard/tickets/pipeline?domain=software',
      testid: 'stat-card-software',
    },
    {
      id: 'kpiProduct',
      label: 'App Support (Open)',
      value: domainBreakdown.product ?? 0,
      icon: Package,
      color: '#10B981',
      to: '/dashboard/tickets/pipeline?domain=product',
      testid: 'stat-card-product',
    },
    {
      id: 'kpiTopProduct',
      label: topProduct ? `Top: ${topProduct.name}` : 'Top product',
      value: topProduct?.count ?? 0,
      icon: Package,
      color: '#002FA7',
      to: topProduct ? `/dashboard/tickets/pipeline?product_id=${encodeURIComponent(topProduct.key)}` : '/dashboard/tickets/pipeline',
      testid: 'stat-card-top-product',
    },
    {
      id: 'kpiTopClient',
      label: topClient ? `Client: ${topClient.name}` : 'Top client',
      value: topClient?.count ?? 0,
      icon: Users,
      color: '#0EA5E9',
      to: topClient ? `/dashboard/tickets/pipeline?client=${encodeURIComponent(topClient.key)}` : '/dashboard/tickets/pipeline',
      testid: 'stat-card-top-client',
    },
    {
      id: 'kpiTeamMembers',
      label: 'Team members',
      value: teamMemberTotal,
      icon: UsersThree,
      color: '#002FA7',
      to: '#team-by-product',
      testid: 'stat-card-team-members',
      onClick: () => {
        if (show('teamChart')) {
          document.getElementById('team-by-product')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          navigate('/dashboard/products');
        }
      },
    },
    {
      id: 'kpiTeamActive',
      label: 'Team active',
      value: teamActiveTotal,
      icon: UsersThree,
      color: '#F59E0B',
      to: '/dashboard/tickets/pipeline',
      testid: 'stat-card-team-active',
    },
    {
      id: 'kpiTeamClosed',
      label: 'Team closed',
      value: teamClosedTotal,
      icon: CheckCircle,
      color: '#6B7280',
      to: `/dashboard/tickets/list${buildLink({ status_group: 'closed' })}`,
      testid: 'stat-card-team-closed',
    },
    {
      id: 'kpiKnowledge',
      label: 'Knowledge Articles',
      value: stats?.total_articles || 0,
      icon: Books,
      color: '#3B82F6',
      to: '/dashboard/knowledge',
      testid: 'stat-card-knowledge-articles',
    },
    {
      id: 'kpiAssets',
      label: 'Total Assets',
      value: stats?.total_assets || 0,
      icon: HardDrives,
      color: '#8B5CF6',
      to: '/dashboard/assets',
      testid: 'stat-card-total-assets',
      disabled: !canSeeOrgAssets,
    },
    {
      id: 'kpiAssetsAssigned',
      label: 'Assets Assigned',
      value: stats?.assets_assigned || 0,
      icon: HardDrives,
      color: '#6366F1',
      to: '/dashboard/assets',
      testid: 'stat-card-assets-assigned',
      disabled: !canSeeEconomics,
    },
    {
      id: 'kpiAssetsInfra',
      label: 'Client Infrastructure',
      value: stats?.assets_infra || 0,
      icon: HardDrives,
      color: '#0EA5E9',
      to: '/dashboard/assets',
      testid: 'stat-card-assets-infra',
      disabled: !canSeeEconomics,
    },
    {
      id: 'kpiAssetPurchase',
      label: 'Asset Purchase Value',
      value: money(stats?.assets_purchase_value || 0),
      icon: HardDrives,
      color: '#D97706',
      to: '/dashboard/assets',
      testid: 'stat-card-asset-purchase',
      disabled: !canSeeEconomics,
    },
    {
      id: 'kpiRevenue',
      label: 'Client Revenue / mo',
      value: money(econSummary.revenue_monthly || 0),
      icon: CurrencyInr,
      color: '#059669',
      to: '/dashboard/assets/economics/clients',
      testid: 'stat-card-revenue',
      disabled: !canSeeEconomics,
    },
    {
      id: 'kpiSpend',
      label: 'Delivery Spend / mo',
      value: money(econSummary.spend_monthly || 0),
      icon: CurrencyInr,
      color: '#DC2626',
      to: '/dashboard/assets/economics/spend-mix',
      testid: 'stat-card-spend',
      disabled: !canSeeEconomics,
    },
    {
      id: 'kpiMargin',
      label: 'Monthly Margin',
      value: money(econSummary.margin_monthly || 0),
      icon: TrendUp,
      color: (econSummary.margin_monthly || 0) >= 0 ? '#002FA7' : '#B91C1C',
      to: '/dashboard/assets/economics/products',
      testid: 'stat-card-margin',
      disabled: !canSeeEconomics,
    },
    {
      id: 'kpiClientsBilled',
      label: 'Clients with Billing',
      value: `${econSummary.clients_with_billing || 0} / ${econSummary.client_count || 0}`,
      icon: Package,
      color: '#0F766E',
      to: '/dashboard/assets/economics/clients',
      testid: 'stat-card-clients-billed',
      disabled: !canSeeEconomics,
    },
    {
      id: 'kpiUsers',
      label: 'Total Users',
      value: stats?.total_users || 0,
      icon: Users,
      color: '#EC4899',
      to: '/dashboard/users',
      testid: 'stat-card-total-users',
      disabled: !canSeeOrgAssets,
    },
  ];

  const kpiById = Object.fromEntries(allKpis.map((k) => [k.id, k]));
  const visibleKpis = kpiOrder
    .map((id) => kpiById[id])
    .filter((k) => k && show(k.id) && !k.disabled);

  const toggleWidget = (id) => {
    savePrefs({
      ...prefs,
      widgets: { ...prefs.widgets, [id]: !show(id) },
    });
  };

  const setViewMode = (viewMode) => {
    savePrefs({ ...prefs, viewMode });
  };

  const setColumnsPerRow = (value) => {
    savePrefs({ ...prefs, columnsPerRow: clampColumns(value) });
  };

  const columnsPerRow = clampColumns(prefs.columnsPerRow ?? DEFAULT_COLS);

  const reorderCharts = (fromId, toId) => {
    savePrefs({ ...prefs, chartOrder: reorderIds(chartOrder, fromId, toId) });
  };

  const reorderKpis = (fromId, toId) => {
    savePrefs({ ...prefs, kpiOrder: reorderIds(kpiOrder, fromId, toId) });
  };

  const goKpi = (stat) => {
    if (typeof stat.onClick === 'function') {
      stat.onClick();
      return;
    }
    if (stat.to && !stat.to.startsWith('#')) navigate(stat.to);
  };

  const renderChart = (id) => {
    if (!show(id)) return null;
    if (SUPER_ADMIN_ONLY_WIDGETS.has(id) && !canSeeEconomics) return null;
    const wrap = (node, { fullWidth = false } = {}) => (
      <div
        key={id}
        className={`wd-drag-item${fullWidth ? ' wd-span-full' : ''}${draggingId === id ? ' is-dragging' : ''}`}
        data-testid={`dash-chart-${id}`}
        {...dragHandlers(id, { onReorder: reorderCharts, setDraggingId })}
      >
        <div className="wd-drag-handle" title="Drag to reorder" aria-hidden>⋮⋮</div>
        {node}
      </div>
    );

    if (id === 'statusChart') {
      return wrap(
        <StatusDonut
          data={statusForChart}
          title="Tickets by status"
          onSliceClick={(key) => navigate(`/dashboard/tickets/list${buildLink({ status: key })}`)}
        />,
      );
    }
    if (id === 'domainChart') {
      return wrap(
        <CategoryPie
          data={
            breakdown.by_domain.length
              ? breakdown.by_domain.map((d) => ({
                key: d.key,
                name: d.name || d.key,
                value: d.count,
              }))
              : domainBreakdown
          }
          title="Tickets by domain"
          subtitle="Click slice or Full view for domain 360°"
          onSliceClick={(key) => drillPipeline({ mode: 'domain', domain: key })}
          headerRight={(
            <ChartFullLink to="/dashboard/tickets/pipeline?mode=domain" />
          )}
        />,
      );
    }
    if (id === 'appChart') {
      return wrap(
        <WorkloadBar
          data={breakdown.by_app}
          title="Tickets by product"
          subtitle="From ticket create · click for product 360°"
          valueLabel="open"
          maxItems={10}
          onBarClick={(key) => drillPipeline({ product_id: key })}
        />,
      );
    }
    if (id === 'clientChart') {
      return wrap(
        <WorkloadBar
          data={breakdown.by_client}
          title="Tickets by client"
          subtitle="Click bar or Full view for client 360°"
          valueLabel="open"
          maxItems={10}
          onBarClick={(key) => drillPipeline({ mode: 'client', client: key })}
          headerRight={(
            <ChartFullLink to="/dashboard/tickets/pipeline?mode=client" />
          )}
        />,
      );
    }
    if (id === 'volumeChart') {
      return wrap(
        <VolumeBar
          data={trend}
          title={`Volume (${TIME_RANGES.find((r) => r.value === filters.timeRange)?.label || 'period'})`}
          headerRight={(
            <ChartFullLink to="/dashboard/tickets/pipeline?mode=volume" />
          )}
          onBarClick={(date) => {
            if (!date) return;
            // trend labels may be short — prefer full date from matching row
            const full = (trend || []).find((t) => t.date === date || (t.date || '').endsWith(date) || (t.date || '').slice(5) === date);
            const createdOn = full?.date || date;
            drillPipeline({ mode: 'volume', created_on: createdOn });
          }}
        />,
      );
    }
    if (id === 'slaChart') {
      return wrap(
        <div
          role="link"
          tabIndex={0}
          data-testid="dashboard-sla-chart"
          onClick={() => navigate('/dashboard/sla?tab=pipeline')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/dashboard/sla?tab=pipeline');
            }
          }}
          style={{ cursor: 'pointer' }}
          aria-label="Open SLA Management"
        >
          <SlaRiskDonut
            data={{
              breached: slaSummary?.breached || stats?.sla_breached || 0,
              at_risk: slaSummary?.at_risk || stats?.sla_at_risk || 0,
              on_track: slaSummary?.on_track || 0,
            }}
            title="SLA risk"
            subtitle={
              slaSummary?.compliance_pct != null
                ? `${slaSummary.compliance_pct}% on track · click to open SLA Management`
                : 'Click a segment to open SLA Management'
            }
            onSliceClick={(key) => {
              const state = key === 'on_track' ? 'all' : (key || 'breached');
              navigate(`/dashboard/sla?tab=pipeline&state=${encodeURIComponent(state)}`);
            }}
          />
        </div>,
      );
    }
    if (id === 'teamChart') {
      return wrap(
        <div id="team-by-product">
          <ProductTeamsWidget items={productTeams} />
        </div>,
      );
    }
    if (id === 'performanceChart') {
      return wrap(
        <div id="performance-by-tickets">
          <PerformanceByTicketsWidget
            items={performance.items}
            maxClosed={performance.max_closed}
          />
        </div>,
      );
    }
    if (id === 'solvedPerMonthChart') {
      return wrap(<SolvedPerMonthWidget />);
    }
    if (id === 'solvedByAppChart') {
      return wrap(<SolvedByApplicationWidget />, { fullWidth: true });
    }
    if (id === 'solvedByPersonChart') {
      return wrap(<SolvedByPersonWidget />, { fullWidth: true });
    }
    if (id === 'econProductChart') {
      const rows = (economics?.by_product || []).map((p) => ({
        label: p.product_name || p.product_id || 'Product',
        created: p.revenue_monthly || 0,
        closed: p.spend_monthly || 0,
        product_id: p.product_id || '__none__',
      }));
      return wrap(
        <VolumeBar
          data={rows}
          title="Revenue vs spend by product"
          subtitle="Click a bar for full product detail"
          createdName="Revenue / mo"
          closedName="Spend / mo"
          onBarClick={(_key, payload) => {
            const pid = payload?.product_id || '__none__';
            navigate(`/dashboard/assets/economics/products/${encodeURIComponent(pid)}`);
          }}
          headerRight={<ChartFullLink to="/dashboard/assets/economics/products" label="Full view" />}
        />,
      );
    }
    if (id === 'econClientChart') {
      const rows = (economics?.by_client || []).slice(0, 12).map((c) => ({
        label: c.client_name || c.client_id,
        created: c.revenue_monthly || 0,
        closed: c.spend_monthly || 0,
        client_id: c.client_id,
      }));
      return wrap(
        <VolumeBar
          data={rows}
          title="Client revenue vs spend"
          subtitle="Click a bar for full client cost breakdown"
          createdName="Revenue / mo"
          closedName="Spend / mo"
          onBarClick={(_key, payload) => {
            if (payload?.client_id) {
              navigate(`/dashboard/assets/economics/clients/${payload.client_id}`);
            } else {
              navigate('/dashboard/assets/economics/clients');
            }
          }}
          headerRight={<ChartFullLink to="/dashboard/assets/economics/clients" label="Full view" />}
        />,
      );
    }
    if (id === 'econMixChart') {
      const byProduct = (economics?.spend_by_product || []).map((p) => ({
        key: p.key || p.product_id || p.name,
        name: p.name || p.product_name || 'Product',
        value: p.value || p.spend_monthly || 0,
      })).filter((d) => d.value > 0);
      // Fallback: derive from by_product if spend_by_product missing (older API)
      const mix = byProduct.length
        ? byProduct
        : (economics?.by_product || [])
          .filter((p) => (p.spend_monthly || 0) > 0)
          .map((p) => ({
            key: p.product_id || '__none__',
            name: p.product_name || p.product_id || 'Product',
            value: p.spend_monthly || 0,
          }));
      return wrap(
        <CategoryPie
          data={mix}
          title="Monthly spend mix"
          subtitle={`All products · total ${money(econSummary.spend_monthly || 0)} / mo · click a product`}
          onSliceClick={(key) => {
            const k = String(key || '');
            if (k) {
              navigate(`/dashboard/assets/economics/spend-mix?product=${encodeURIComponent(k)}`);
            } else {
              navigate('/dashboard/assets/economics/spend-mix');
            }
          }}
          headerRight={<ChartFullLink to="/dashboard/assets/economics/spend-mix" label="Full view" />}
        />,
      );
    }
    if (id === 'assetTypeChart') {
      const byType = economics?.assets?.by_type || {};
      const data = Object.entries(byType).map(([key, value]) => ({
        key,
        name: key.replace(/_/g, ' '),
        value,
      }));
      return wrap(
        <CategoryPie
          data={data}
          title="Assets by type"
          subtitle="Inventory + client infrastructure"
          onSliceClick={() => navigate('/dashboard/assets')}
          headerRight={<ChartFullLink to="/dashboard/assets" label="Inventory" />}
        />,
      );
    }
    if (id === 'assetStatusChart') {
      const byStatusMap = economics?.assets?.by_status || {};
      return wrap(
        <StatusDonut
          data={byStatusMap}
          title="Assets by status"
          onSliceClick={() => navigate('/dashboard/assets')}
        />,
      );
    }
    return null;
  };

  if (loading && !stats) {
    return (
      <ModuleShell title="Dashboard Overview" subtitle={`Welcome back, ${user?.name || ''}`}>
        <SkeletonBlock rows={8} />
      </ModuleShell>
    );
  }

  return (
    <ModuleShell
      title="Dashboard Overview"
      subtitle={`Welcome back, ${user?.name || ''}`}
      icon={Ticket}
      headerActions={(
        <button
          type="button"
          className="tk-btn tk-btn-secondary"
          onClick={() => setShowCustomize((v) => !v)}
          data-testid="dashboard-customize-button"
        >
          <GearSix size={16} />
          Customize
        </button>
      )}
    >
      <div data-testid="overview-page">
        {showCustomize && (
          <div className="wd-dash-customize" data-testid="dashboard-customize-panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Dashboard customization</div>
                <p style={{ margin: 0, fontSize: 12, color: '#6B7280' }}>
                  Toggle metrics, then drag cards on the dashboard to set top / left / right order.
                </p>
              </div>
              <div className="wd-view-toggle" role="group" aria-label="Dashboard view type">
                {VIEW_MODES.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    className={mode === v.id ? 'is-active' : ''}
                    onClick={() => setViewMode(v.id)}
                    title={v.desc}
                    data-testid={`dashboard-view-${v.id}`}
                  >
                    <v.icon size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                  Cards per row
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="number"
                    min={MIN_COLS}
                    max={MAX_COLS}
                    step={1}
                    value={columnsPerRow}
                    onChange={(e) => setColumnsPerRow(e.target.value)}
                    className="input-field"
                    style={{ width: 72, padding: '6px 8px', fontSize: 13 }}
                    data-testid="dashboard-columns-per-row"
                    aria-label="Cards per row"
                  />
                  <span style={{ fontSize: 12, color: '#6B7280' }}>
                    1–{MAX_COLS} · default {DEFAULT_COLS} · cards resize to fit
                  </span>
                </div>
              </div>
              <div className="wd-col-presets" role="group" aria-label="Column presets">
                {[1, 2, 3, 4].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={columnsPerRow === n ? 'is-active' : ''}
                    onClick={() => setColumnsPerRow(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginTop: 12, fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Charts / panels to show
            </div>
            <div className="wd-dash-customize-grid">
              {WIDGET_DEFS.filter((w) => w.group === 'charts')
                .filter((w) => canSeeEconomics || !SUPER_ADMIN_ONLY_WIDGETS.has(w.id))
                .map((w) => (
                <label key={w.id}>
                  <input type="checkbox" checked={show(w.id)} onChange={() => toggleWidget(w.id)} />
                  {w.label}
                </label>
              ))}
            </div>

            <div style={{ marginTop: 12, fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Tiny KPIs to show (each drills into pipeline / details)
            </div>
            <div className="wd-dash-customize-grid">
              {WIDGET_DEFS.filter((w) => w.group === 'kpis')
                .filter((w) => canSeeEconomics || !SUPER_ADMIN_ONLY_WIDGETS.has(w.id))
                .map((w) => (
                <label key={w.id}>
                  <input type="checkbox" checked={show(w.id)} onChange={() => toggleWidget(w.id)} />
                  {w.label}
                </label>
              ))}
            </div>

            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="tk-btn tk-btn-secondary"
                onClick={() => savePrefs({
                  ...DEFAULT_PREFS,
                  widgets: { ...DEFAULT_PREFS.widgets },
                  chartOrder: [...DEFAULT_PREFS.chartOrder],
                  kpiOrder: [...DEFAULT_PREFS.kpiOrder],
                  columnsPerRow: DEFAULT_COLS,
                })}
              >
                Reset defaults
              </button>
              <button type="button" className="tk-btn tk-btn-secondary" onClick={() => setShowCustomize(false)}>
                Done
              </button>
            </div>
          </div>
        )}

        <div
          className="wd-soft-card"
          style={{ marginBottom: 12 }}
          data-testid="dashboard-filter-bar"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <FunnelSimple size={18} weight="bold" />
            <span style={{ fontSize: '12px', fontWeight: '600' }}>Filters</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Priority</label>
              <select
                value={filters.priority}
                onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                className="input-field"
                style={{ padding: '6px 8px', fontSize: '12px' }}
                data-testid="dashboard-filter-priority"
              >
                <option value="all">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>Time Range</label>
              <select
                value={filters.timeRange}
                onChange={(e) => setFilters({ ...filters, timeRange: e.target.value })}
                className="input-field"
                style={{ padding: '6px 8px', fontSize: '12px' }}
                data-testid="dashboard-filter-time-range"
              >
                {TIME_RANGES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
          </div>
          <FilterChips
            chips={filterChips}
            onClearAll={() => setFilters({ priority: 'all', timeRange: 'all' })}
          />
        </div>

        {showTinyKpis && visibleKpis.length > 0 && (
          <div className="wd-compact-kpis" data-testid="dashboard-compact-kpis">
            {visibleKpis.map((stat) => (
              <button
                key={stat.id}
                type="button"
                className={`wd-compact-kpi wd-drag-item${draggingId === stat.id ? ' is-dragging' : ''}`}
                onClick={() => goKpi(stat)}
                data-testid={stat.testid}
                title="Click to drill down · drag to reorder"
                {...dragHandlers(stat.id, { onReorder: reorderKpis, setDraggingId })}
              >
                <span className="wd-drag-handle wd-drag-handle-inline" aria-hidden>⋮⋮</span>
                <span className="wd-compact-kpi-label">{stat.label}</span>
                <span className="wd-compact-kpi-value">{stat.value}</span>
              </button>
            ))}
          </div>
        )}

        {showCharts && (
          <div
            className="wd-chart-grid"
            style={{ '--wd-dash-cols': columnsPerRow }}
            data-cols={columnsPerRow}
          >
            {chartOrder.map((id) => renderChart(id))}
          </div>
        )}

        {showCards && (breakdown.by_app.length > 0 || breakdown.by_client.length > 0) && (
          <div style={{ marginBottom: 16 }} data-testid="dashboard-breakdown-cards">
            {breakdown.by_app.length > 0 && (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Top products — click for 360°
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 14 }}>
                  {breakdown.by_app.slice(0, 6).map((app) => (
                    <button
                      key={app.key}
                      type="button"
                      className="stat-card"
                      style={{ cursor: 'pointer', textAlign: 'left', width: '100%' }}
                      onClick={() => drillPipeline({ product_id: app.key })}
                      data-testid={`stat-card-app-${app.key}`}
                    >
                      <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6B7280', fontWeight: 600, margin: '0 0 6px' }}>
                        Product
                      </p>
                      <p style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>{app.name}</p>
                      <p style={{ fontSize: 18, fontWeight: 700, color: '#002FA7', margin: 0 }}>{app.count}</p>
                      <p style={{ marginTop: 6, fontSize: 12, color: '#002FA7', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                        Pipeline <CaretRight size={12} weight="bold" />
                      </p>
                    </button>
                  ))}
                </div>
              </>
            )}
            {breakdown.by_client.length > 0 && (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Top clients — click for pipeline
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                  {breakdown.by_client.slice(0, 6).map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      className="stat-card"
                      style={{ cursor: 'pointer', textAlign: 'left', width: '100%' }}
                      onClick={() => drillPipeline({ mode: 'client', client: c.key })}
                      data-testid={`stat-card-client-${c.key}`}
                    >
                      <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6B7280', fontWeight: 600, margin: '0 0 6px' }}>
                        Client
                      </p>
                      <p style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>{c.name}</p>
                      <p style={{ fontSize: 18, fontWeight: 700, color: '#002FA7', margin: 0 }}>{c.count}</p>
                      <p style={{ marginTop: 6, fontSize: 12, color: '#002FA7', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                        Pipeline <CaretRight size={12} weight="bold" />
                      </p>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {showCards && visibleKpis.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            {visibleKpis.map((stat) => (
              <div
                key={stat.id}
                role="link"
                tabIndex={0}
                onClick={() => goKpi(stat)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    goKpi(stat);
                  }
                }}
                className={`stat-card wd-drag-item${draggingId === stat.id ? ' is-dragging' : ''}`}
                data-testid={stat.testid}
                style={{ cursor: 'pointer' }}
                aria-label={`View ${stat.label}`}
                {...dragHandlers(stat.id, { onReorder: reorderKpis, setDraggingId })}
              >
                <div className="wd-drag-handle" title="Drag to reorder" aria-hidden>⋮⋮</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <p style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6B7280', fontWeight: '600', marginBottom: '8px' }}>
                      {stat.label}
                    </p>
                    <p style={{ fontSize: '18px', fontWeight: '700', color: '#111827' }}>{stat.value}</p>
                    <p
                      style={{ marginTop: '8px', fontSize: '12px', color: '#002FA7', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}
                      data-testid={`${stat.testid}-link`}
                    >
                      View details <CaretRight size={12} weight="bold" />
                    </p>
                  </div>
                  <div style={{ background: stat.color, padding: '12px', borderRadius: '12px' }}>
                    <stat.icon size={24} color="white" weight="bold" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ModuleShell>
  );
}

export default OverviewPage;
